import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import InstallPrompt from "@/components/InstallPrompt";
import {
  LayoutDashboard, Package, PlusCircle, Building2, Users, BarChart3,
  ScrollText, Settings as SettingsIcon, User as UserIcon, LogOut, Menu,
  ChevronsLeft, X, Receipt,
} from "lucide-react";

const NAVS = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/orders", label: "All Orders", icon: Package },
    { to: "/orders/new", label: "Create Order", icon: PlusCircle },
    { to: "/clients", label: "Clients", icon: Building2 },
    { to: "/staff", label: "Staff", icon: Users },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/billing", label: "Billing", icon: Receipt },
    { to: "/audit-logs", label: "Audit Logs", icon: ScrollText },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
    { to: "/profile", label: "Profile", icon: UserIcon },
  ],
  staff: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/orders/new", label: "Create Order", icon: PlusCircle },
    { to: "/orders", label: "Orders", icon: Package },
    { to: "/profile", label: "Profile", icon: UserIcon },
  ],
  client: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/my-orders", label: "My Orders", icon: Package },
    { to: "/billing", label: "Billing", icon: Receipt },
    { to: "/profile", label: "Profile", icon: UserIcon },
  ],
};

const ROLE_STYLES = {
  admin: "bg-[#E63946]/10 text-[#E63946] border-[#E63946]/30",
  staff: "bg-[#1B3A6B]/10 text-[#1B3A6B] border-[#1B3A6B]/30",
  client: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = NAVS[user?.role] || [];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navContent = (isMobile) => (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
      {items.map((item) => (
        <NavLink
          key={item.to + item.label}
          to={item.to}
          end={item.to === "/dashboard"}
          onClick={() => isMobile && setMobileOpen(false)}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-150 ${
              isActive
                ? "bg-primary text-white"
                : "text-slate-300 hover:bg-white/10 hover:text-white"
            } ${collapsed && !isMobile ? "justify-center px-0" : ""}`
          }
          title={item.label}
        >
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          {(!collapsed || isMobile) && <span className="truncate">{item.label}</span>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className={`hidden md:flex fixed inset-y-0 left-0 z-30 flex-col bg-[#101E38] transition-[width] duration-200 ${
          collapsed ? "w-16" : "w-64"
        }`}
      >
        <div className={`flex items-center h-16 border-b border-white/10 ${collapsed ? "justify-center px-2" : "px-4"}`}>
          {collapsed ? (
            <div className="h-9 w-9 rounded-sm bg-[#E63946] flex items-center justify-center text-white font-bold italic text-lg">E</div>
          ) : (
            <img src="/ezship-logo.webp" alt="EZShip" className="h-9 w-auto bg-white rounded-sm px-2 py-1" data-testid="sidebar-logo" />
          )}
        </div>
        {navContent(false)}
        <div className="border-t border-white/10 p-3">
          <button
            data-testid="sidebar-collapse-button"
            onClick={() => setCollapsed(!collapsed)}
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-sm text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-colors duration-150 ${collapsed ? "justify-center px-0" : ""}`}
          >
            <ChevronsLeft className={`h-4 w-4 transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#101E38] flex flex-col" data-testid="mobile-sidebar">
            <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
              <img src="/ezship-logo.webp" alt="EZShip" className="h-9 min-w-[120px] object-contain bg-white rounded-sm px-2 py-1" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-sidebar-close" className="text-slate-300 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            {navContent(true)}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className={`flex flex-col min-h-screen transition-[margin] duration-200 ${collapsed ? "md:ml-16" : "md:ml-64"}`}>
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 md:px-6" data-testid="header">
          <button
            className="md:hidden text-slate-600 hover:text-slate-900"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-semibold text-slate-900 leading-tight" data-testid="header-user-name">{user?.name}</div>
              <span
                data-testid="header-user-role"
                className={`inline-block mt-0.5 px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider border ${ROLE_STYLES[user?.role] || ROLE_STYLES.client}`}
              >
                {user?.role}
              </span>
            </div>
            <Avatar name={user?.name} userId={user?.id} avatarPath={user?.avatar_path} testId="header-avatar" />
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              title="Log out"
              className="ml-1 p-2 rounded-sm text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-colors duration-150"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <InstallPrompt />
    </div>
  );
}
